import React from 'react';
import { Paper, Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

interface SubscriptionBannerProps {
  isPro: boolean;
  onUpgradeClick: () => void;
}

export const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({ isPro, onUpgradeClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isPro) return null;

  return (
    <Paper 
      sx={{ 
        p: { xs: 2.5, sm: 3 },
        mb: 4,
        background: 'linear-gradient(135deg, #2A2F6F 0%, #0F1642 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: '16px', sm: '24px' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          p: 2,
          transform: 'rotate(45deg) translate(25%, -50%)',
          opacity: 0.1,
        }}
      >
        <StarIcon sx={{ fontSize: { xs: 200, sm: 250 } }} />
      </Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        position: 'relative',
        zIndex: 1,
        gap: { xs: 3, sm: 4 }
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            fontWeight="bold"
            sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              mb: { xs: 1, sm: 2 }
            }}
          >
            Upgrade to Pro
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2, 
              opacity: 0.9,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              lineHeight: 1.6
            }}
          >
            Unlock premium features to create perfect email templates
          </Typography>
          <List dense sx={{ mb: { xs: 2, sm: 0 } }}>
            {[
              'All email clients (Apple Mail, Yahoo, Thunderbird, etc.)',
              'Custom device sizes and responsive testing',
              'AI-powered content analysis',
              'Unlimited email templates',
              'Priority support',
              'Export without watermark'
            ].map((feature, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: '#8B5CF6' }} />
                </ListItemIcon>
                <ListItemText 
                  primary={feature}
                  primaryTypographyProps={{ 
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    lineHeight: { xs: 1.4, sm: 1.6 }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          alignItems: { xs: 'stretch', sm: 'flex-end' }
        }}>
          <Button 
            fullWidth={isMobile}
            variant="contained"
            size={isMobile ? "large" : "medium"}
            onClick={onUpgradeClick}
            sx={{ 
              background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
              color: 'white',
              py: { xs: 1.5, sm: 1.25 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '1rem', sm: '0.9rem' },
              fontWeight: 600,
              borderRadius: '12px',
              '&:hover': {
                background: 'linear-gradient(90deg, #5558E6 0%, #7C4DEF 100%)',
              },
              boxShadow: '0 8px 16px rgba(99, 102, 241, 0.2)',
            }}
          >
            Upgrade Now
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}; 